import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  Inject,
  inject,
  OnInit,
  signal,
  untracked,
  ViewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { ChartModule, UIChart } from 'primeng/chart';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { CompanyEarningsStore } from './stores/company-earnings.store';
import {
  ChartDto,
  ChartPointDto,
  ChartSeriesDto,
  DatePeriod,
  LegendChip,
} from 'src/app/shared/models/chart.model';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { timeSeriesToChart } from 'src/app/shared/mappers/time-series-to-chart.mapper';
import { TimeSeriesResponseDto } from 'src/app/shared/models/time-series-response.model';
import { TreeTableModule } from 'primeng/treetable';
import { TreeNode } from 'primeng/api';
import { AppTableLoadingOverlayComponent } from "src/app/shared/components/loading-overlay/table-loading-overlay.component";
import { UserStore } from 'src/app/shared/user/store/user.store';

@Component({
  selector: 'app-company-earnings-widget',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ChartModule,
    SelectModule,
    TableModule,
    ButtonModule,
    TreeTableModule,
    TagModule,
    AppTableLoadingOverlayComponent
],
  templateUrl: './company-earnings-widget.component.html',
  styleUrls: ['./company-earnings-widget.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class CompanyEarningsWidgetComponent implements OnInit {
@ViewChild('chartRef') chartRef?: UIChart;

  protected readonly store = inject(CompanyEarningsStore);
   protected readonly userStore = inject(UserStore);

  widgetHeader: WritableSignal<string> = signal('');
  earningsByCostCenterTreeNode: WritableSignal<TreeNode[]> = signal([]);
  legendChips: LegendChip[] = [];
  

  constructor(@Inject('label') public label: any) {
    this.widgetHeader.set(this.label);
    effect(() => {
      this.userStore.activeTenant();
      this.userStore.dashboardRefreshTrigger();
      untracked(() => this.loadData());
    });


    effect(() => {
      const dto = this.store.data() as TimeSeriesResponseDto | null;
      if (!dto) return;
      const chart = timeSeriesToChart(dto, {
        chartType: 'combo',
        seriesTypeByKey: { actual: 'bar', target: 'line' },
      });
      this.buildChart(chart);
    });
  }

  rangeOptions: { label: string; value: DatePeriod }[] = [
    { label: 'Tage', value: DatePeriod.FourteenDays },
    { label: 'Wochen', value: DatePeriod.TenWeeks },
    { label: 'Monate', value: DatePeriod.ThirteenMonths },
    { label: 'Quartale', value: DatePeriod.EightQuarter },
    { label: 'Jahre', value: DatePeriod.AllYears },
  ];

  rangeControl = new FormControl<DatePeriod>(DatePeriod.ThirteenMonths, {
    nonNullable: true,
  });

  data = signal<any>(null);
  options = signal<any>(null);
  plugins = signal<any[]>([]);

  rangeLabel = computed(() => {
    const opt = this.rangeOptions.find(
      (x) => x.value === this.rangeControl.value,
    );
    return opt?.label ?? '';
  });

  private readonly fmt = (n: number) =>
    new Intl.NumberFormat('de-DE').format(n);

  ngOnInit(): void {    
    this.rangeControl.valueChanges.subscribe((range) => {
      this.store.load(range);
    });
  }

  private loadData() {
    this.store.load(this.rangeControl.value);
  }

  onNodeExpand(event: { node: TreeNode }): void {
    const node = event.node;
    // Nur laden wenn Kinder noch nie geladen wurden
    if (!node.data?.childrenLoaded) {
      this.store.loadChildren(node);
    }
  }

  private buildChart(dto: ChartDto) {
    const labels = dto.points.map((p) => {
      const l = p.labels ?? {};
      return l['labelDay'] ?? l['labelDate'] ?? String(p.x);
    });

    // Datasets generisch aus dto.series bauen
    const datasets = dto.series.map((s, index) => this.toDataset(dto.points, s, index));

    // Delta-Plugin (generisch)
    this.buildLegendChips(dto);

    const deltaPlugin = this.createDeltaPlugin(dto);
    this.plugins.set(deltaPlugin ? [deltaPlugin] : []);

    this.data.set({
      labels,
      datasets,
    });

    this.options.set(this.buildOptions(dto));
  }

 toggleDataset(chip: LegendChip): void {
  const chart = this.chartRef?.chart;
  if (!chart) return;

  const visible = chart.isDatasetVisible(chip.datasetIndex);
  chart.setDatasetVisibility(chip.datasetIndex, !visible);
  chart.update();

  chip.hidden = visible;
  // Falls OnPush + plain array manchmal nicht sofort rendert:
  this.legendChips = [...this.legendChips];
}

  private toDataset(points: ChartPointDto[], series: ChartSeriesDto, seriesIndex: number) {
  const data = points.map((p) => p.values?.[series.key] ?? null);
  const color = this.getSeriesColor(series.key, seriesIndex);

  const base: any = {
    _seriesKey: series.key,
    label: series.label,
    data,
  };

  if (series.type === 'bar') {
    return {
      ...base,
      type: 'bar',
      backgroundColor: this.withAlpha(color, 1),
      borderColor: color,
      borderRadius: 4,
      barThickness: 34,
      order: 2,
    };
  }

  if (series.type === 'line') {
    return {
      ...base,
      type: 'line',
      borderColor: color,
      backgroundColor: this.withAlpha(color, 0.15),
      pointBackgroundColor: color,
      pointBorderColor: '#fff',
      pointRadius: 4,
      pointBorderWidth: 1,
      borderWidth: 0.7,
      //borderDash: series.key === 'target' ? [5, 5] : undefined,
      tension: 0.4,
      order: 1,
    };
  }

  return {
    ...base,
    type: series.type ?? 'line',
    borderColor: color,
    backgroundColor: this.withAlpha(color, 0.2),
  };
}
protected withAlpha(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
  private buildLegendChips(dto: ChartDto) {
     this.legendChips = dto.series.map((s, index) => ({
      label: s.label,
      color: this.getSeriesColor(s.key, index),
      
      datasetIndex: index,
      hidden: false,
    }));
  }
private readonly seriesColorMap: Record<string, string> = {
  actual: '#387EF7',
  target: '#000000',
  // weitere bekannte Serien:
  forecast: '#10b981',
  previous: '#f59e0b',
};

private getSeriesColor(seriesKey: string, index: number): string {
  return this.seriesColorMap[seriesKey] ?? '#94a3b8'; // neutraler Fallback
}

  private buildOptions(dto: ChartDto) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 1,
      animation: true,
      layout: {
        padding: { top: 12, right: 0, bottom: 36, left: 0 },
      },
      plugins: {
       legend: {
    display: false,
  },
  tooltip: { enabled: true },
},
      scales: {
        x: {
          position: 'bottom',
          grid: { display: false },
          ticks: {
            color: '#111',
            font: { size: 12, weight: '600' },
          },
          border: { display: true },
        },
        y: {
          beginAtZero: true,
          title: {
            display: false,
            text: 'Umsatz in €',
            color: '#64748b',
            font: { size: 10, weight: '400', family: 'Arial' },
          },
          ticks: {
            display: false,
            color: '#64748b',
            padding: 8,
    
           // maxTicksLimit: 4,
           // callback: (v: any) => `${Number(v).toLocaleString('de-DE')} €`,
          },
          grid: {
            color: 'rgba(0,0,0,0.08)',
            borderDash: [4, 4],
          },
          border: {
            display: false, // Achsenlinie
          },
        },
      },
    };
  }

  /**
   * Delta Plugin: zeigt unter der X-Achse Δ zwischen zwei Serien.
   * Konfiguration:
   *  dto.meta.delta = { a: "actual", b: "target" }
   * Wenn nicht gesetzt: nimmt series[0] und series[1]
   */
  private createDeltaPlugin(dto: ChartDto) {
    const meta = dto.meta as any;

    const seriesKeys = dto.series.map((s) => s.key);
    const a = (meta?.delta?.a as string | undefined) ?? seriesKeys[0];
    const b = (meta?.delta?.b as string | undefined) ?? seriesKeys[1];

    if (!a || !b) return null;

    return {
      id: 'bottomLabels',
      afterDraw: (chart: any) => {
        const { ctx, chartArea, scales } = chart;
        const x = scales.x;
        const yDelta = chartArea.bottom + 35;
        const yDeltaSub = chartArea.bottom + 50;

        const dsA = chart.data.datasets?.find((d: any) => d?._seriesKey === a);
        const dsB = chart.data.datasets?.find((d: any) => d?._seriesKey === b);

        if (!dsA || !dsB) return;

        const aData = (dsA.data ?? []) as Array<number | null>;
        const bData = (dsB.data ?? []) as Array<number | null>;

        const tickCount = x.ticks.length;

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '500 10px system-ui, -apple-system, Segoe UI, Roboto, Arial';

        for (let i = 0; i < tickCount; i++) {
          const xPos = x.getPixelForTick(i);

          const av = aData[i];
          const bv = bData[i];

          // wenn einer null ist , delta nicht anzeigen oder z.B. "–"
          if (av == null || bv == null) {
            ctx.fillStyle = '#64748b';
            ctx.fillText('–', xPos, yDelta);
            continue;
          }

         const dAbs = Number(av) - Number(bv);

          // Edge case: bv = 0 -> Prozent nicht definierbar
          if (Number(bv) === 0) {
            ctx.fillStyle = '#64748b';
            ctx.fillText('–', xPos, yDeltaSub);
            continue;
          }

          // Prozent delta (gegen bv/target)
          const dPct = (dAbs / Number(bv)) * 100;

          const up = dAbs >= 0;
         // const arrow = up ? '▲' : '▼';
          const arrow = up ? '↑' : '↓';

          ctx.fillStyle = 'black';
          ctx.fillText(`${this.fmt(av)} €`, xPos, yDelta);

          ctx.fillStyle = up ? '#22a06b' : '#d6453d';
          ctx.fillText(
            `${arrow} ${up ? '+' : ''}${dPct.toFixed(1)} %`,
            xPos,
            yDeltaSub,
          );
        }

        ctx.restore();
      },
    };
  }
}
